import config from "appConfig";

import { getLinkForCourseLesson } from "../../../../services/Utils";
import lesson0505 from "../lesson-05/lesson";
import lesson0506 from "../lesson-06/lesson";
import lesson0511 from "../lesson-11/lesson";
import lesson0513 from "../lesson-13/lesson";
import lesson0514 from "../lesson-14/lesson";
import lesson0529 from "../lesson-29/lesson";

const linkLesson0505 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0505.id);
const linkLesson0506 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0506.id);
const linkLesson0511 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0511.id);
const linkLesson0513 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0513.id);
const linkLesson0514 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0514.id);
const linkLesson0529 = getLinkForCourseLesson("53e2fab0-edf6-4634-a20e-ae0cdfe73cba", lesson0529.id);

/* eslint-disable */
const theory = ` <h3 class=sectionHead>What will you learn in this lesson?</h3>
<p>In this lesson, you will learn what ”good acoustics” fundamentally means and why it is important to identify the use or uses of a space when designing acoustics for a room.
<h3 class=sectionHead>Introduction</h3>
<p>If there is one general statement to be made on the complex subject of “good acoustics” in a room, it should be above all that the acoustics should be suited to the use (or uses) of the room. To give just one example: a room can have great acoustics for (classical) music concerts, but be acoustically unsatisfactory for (unamplified) speech, or vice versa. The acoustic requirements to obtain good acoustic conditions are fully dependent on the program/events the room is or will be used for. It is therefore impossible to make any acoustic judgments on a room without making the connection to its use. Another implication is that if a room has multiple uses, it can only have good acoustics for one of them, unless the acoustics can be made adaptable to each use. In other words, “good acoustics” as such does not exist.<br class=newline>
<p>The main requirements for good acoustics in rooms dedicated to speech uses are discussed in <span class=cmti-10><a href=${linkLesson0513} target="_blank">Lesson 5.13</a></span>. Rooms intended for music use are treated in <span class=cmti-10><a href=${linkLesson0514} target="_blank">Lesson 5.14</a></span>.<br class=newline>In the present lesson, we will focus on an often overlooked universal but fundamental acoustic requirement: the need to obtain suitable loudness in a room.<br class=newline><br class=newline>Besides room acoustics, the level of the background noise is also an important factor that needs to be adapted to the use of a particular room. This subject is dealt with in <span class=cmti-10><a href=${linkLesson0529} target="_blank">Lesson 5.29</a></span>.<br class=newline>
<h3 class=sectionHead>The need to obtain suitable loudness</h3>
<p>
   It may sound evident, but a fundamental requirement at all times for good acoustics in a room is that the source(s) do not sound excessively loud, nor too weak in the room. If the sound is too weak, the content or message, whether it is speech or music, will not be perceived properly. Of course, the talker or musician can raise their effort (in physical terms: their sound power level) to be better heard, but they will be able to do so only over a certain period of time before fatigue starts. On the other hand, if the sound is too loud, it may cause discomfort and/or even lead to hearing damage in case of prolonged exposure. To give an example, even unamplified singing by a choir can be unpleasantly loud if the room is too small and too reverberant. On the other hand, if a small room is excessively dampened, the loudness may be adapted well for the choir itself, but the reverberation will be too short. The choir and the audience will be deprived of a nice musical experience, and the choir members may even have to strain their voices to compensate for the lack of support of the room. See <span class=cmti-10><a href=${linkLesson0514} target="_blank">Lesson 5.14</a></span> for more details.<br class=newline><br class=newline>Loudness in a room (or more precisely: the sound pressure level
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Lp</mi>
   </math>
   ) is closely linked to the sound power level
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Lw</mi>
   </math>
   of the source(s) and the total amount of acoustic absorption (equivalent absorption area A) present in the room. Here we assume that we are at a sufficient distance from the source, at which the reverberant field is stronger than the direct field. This is often the case in reality. As explained in <span class=cmti-10><a href=${linkLesson0506} target="_blank">Lesson 5.6</a></span> (see equation 21):
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>w</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mn>10</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mi>A</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <mn>6</mn>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   This may seem like a surprisingly simple equation, as at first sight it does not seem to take into account the size or volume of the room. But try looking at it in the following thought exercise: if the materials placed on the surfaces of a room are kept unchanged (i.e. with a constant absorption coefficient
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>α</mi>
   </math>
   ) as the room volume grows, the surface areas (S) will also grow and A will increase, since
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>A</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>S</mi>
      <mo class=MathClass-bin>∗</mo>
      <mi>α</mi>
   </math>
   . Therefore, the equivalent absorption area does, in fact, factor in the “size” of the room.<br class=newline>Also, by expressing A from the Sabine equation (see <span class=cmti-10><a href=${linkLesson0505} target="_blank">Lesson 5.5</a></span>) and introducing it into the above equation, we get the sound pressure level of reverberant sound that is explicitly dependant on the room volume V and reverberation time T:
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>p</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>w</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mn>10</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mi>V</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <mn>10</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mi>T</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <mn>14</mn>
         </math>
      <td class=eq-no>(2)
</table>
<p>Equation 2 establishes the important link between the theoretical loudness in the room, the sound power of the sources, the volume of the room and the reverberation time. Of course, the real loudness can vary in a room, depending on the distance to the source and on the positions of absorbing materials on the surfaces of the room.<br class=newline><br class=newline>
<p>
   The reader may simply shrug their shoulders at this simple equation, but it is essential for understanding good acoustics. There is a large discrepancy between the broad range of sound power levels
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Lw</mi>
   </math>
   of different sources and the broad range of possible room sizes represented with their volume V (and, to a lesser extent, the reverberation time T), and the rather limited range of the resulting sound pressure level
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Lp</mi>
   </math>
   in the room the listeners find acceptable. For an acoustician, it is crucial to be aware of this discrepancy and to design the room according to its intended use, so that appropriate sound pressure level (loudness) is achieved.<br class=newline>
<p>
   Let us start by looking at the sound power level
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Lw</mi>
   </math>
   of some typical natural (i.e. unamplified) sound sources in the following table. The table shows both the unweighted and the A-weighted values of the sound power level. The difference between these values rarely exceeds a few decibels for these unamplified sources, as they do not emit sound with a strong low-frequency content.<br class=newline>
<div class=table>
  <figure>
    <figcaption aria-hidden="true">Table 1. Sound Power Levels for Different Types of Sources.</figcaption>
    <img alt="Table 1. Sound Power Levels for Different Types of Sources" src="${config.ASSETS_URL}/05/10/theory/table-1.svg" />
  </figure>
</div>
<p>The extreme values in the table show that the sound power level of a full symphony orchestra is 46 dB higher than the one of a single talker speaking in normal voice. In terms of sound power, this means that the orchestra is 40000 times more powerful than a single talking person. This is a broad range of sound power (levels) to deal with.<br class=newline>
<p>
   To achieve comfortable listening conditions at the receiving end, the sound pressure levels
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Lp</mi>
   </math>
   should fall between approximately 50 and 85 dB(A). Below this range, the sound is usually too weak to convey information. Above this range, sound is loud enough to possibly cause hearing damage. Therefore, at the receiving end, the range of acceptable sound pressure levels is only 35 dB, i.e. 10 dB narrower than the range of sound power levels of the sound sources, as shown in the above table. This already explains why the same room cannot be equally good for symphonic music performances and unamplified speech. It should be noted that the range of acceptable sound pressure levels is narrowed even more by the presence of background noise in the room.<br class=newline>
<p>
   Now let us consider the range of room sizes that we encounter in our world. A typical living room can have a volume of 100 m<sup>3</sup>, a small auditorium 1000 m<sup>3</sup>, and a concert hall 10000 m<sup>3</sup>. According to equation 2, every 10-fold increase of the room volume (while keeping sound power and reverberation time constant) corresponds to a 10 dB reduction of the sound pressure level
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Lp</mi>
   </math>
   , and vice versa. The room volume clearly has a very important impact on the loudness, but the typical range associated with it is smaller than the range of the sound power levels of sound sources.<br class=newline>
<p>
   The influence of reverberation time T on the sound pressure level in the room is modest at best. Its typical range begins at about 0.5 seconds (living space, office space, studios) to 5 seconds (cathedrals, large sports halls, etc.), which would suggest a corresponding possible change of the sound pressure level of 10 dB. However, for a constant room volume (which interests us here), i.e. in the same room, it is a practical challenge to vary the reverberation time even by a factor of 2 by means of ”variable acoustics”, which corresponds to ”only” 3 dB change in
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Lp</mi>
   </math>
   .<br class=newline>
<p>The proper course of action is to choose the volume of the room in the design stage, so that it is compatible with the sound power levels of the expected sound sources in the room, depending on its use. If the room is already built, its use must be chosen/adapted to match the room volume. Both the investors/clients and the architects must be (made) aware of this. Secondly, the reverberation time can only be used for “fine tweaking” of the loudness within a limited range. But there are other acoustic considerations that come into play. For example, reverberation facilitates blending of individually played notes together into an enjoyable line of music, and is, therefore, an essential acoustic feature of a performance space for classical music. On the other hand, reverberation will cause an overlap (in time) of individual phonemes and syllables of speech. In this case, it should be carefully controlled not to hinder speech intelligibility.<br class=newline>
<p>
   In the principle part of this lesson, shown to the right, you can listen to three types of sources (a female voice, a flute player and an orchestra) in three rooms with different room volume (100 m<sup>3</sup>, 1000 m<sup>3</sup> and 10000 m<sup>3</sup>). The reverberation time can also be adjusted (0.7 s, 1 s and 2 s). The corresponding sound pressure level
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Lp</mi>
   </math>
   is also indicated for each case. The sound pressure levels of sample sounds, i.e. the differences between them from one case to the next are realistic, so other than hearing the difference in reverberation, you can experience how the loudness changes depending on the chosen source (
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Lw</mi>
   </math>
   ), the size of the room (room volume V) and/or reverberation time T. In practice, certain combinations will never work well, as the sound pressure level will either be too low (for example, the human voice in the largest room), or too high (an entire orchestra in the smallest room), regardless of reverberation time.<br class=newline>
<h3 class=sectionHead>Other requirements</h3>
<p>Obtaining suitable loudness in a room is a necessary condition for good acoustics, but not sufficient by itself. Depending on the use of the room, there are many other requirements that need to be addressed, such as obtaining good speech intelligibility in lecture rooms (see <span class=cmti-10><a href=${linkLesson0513} target="_blank">Lesson 5.13</a></span>). This requires a sufficient amount of early reflections that aid the direct sound, along with tightly controlled reverberation. In concert halls, the reverberation should be tailored (in time, strength, space and frequency) to the type of music and should be balanced with early reflections that arrive at the listening positions (preferably laterally), thus achieving a suitable combination of source presence and room presence (see <span class=cmti-10><a href=${linkLesson0514} target="_blank">Lesson 5.14</a></span>).<br class=newline>
<p><span class=cmti-10><a href=${linkLesson0511} target="_blank">Lesson 5.11</a></span> introduces a set of objective and subjective parameters that can be used in room acoustic design and evaluation. However, these parameters should be considered as “first handles” only, as they do not describe the acoustics of rooms in all its complexity, nor do they capture its nuances.<br class=newline>
<p>As presented in this lesson, the amount of absorption is an important parameter to consider in controlling loudness. In addition, the positioning/distribution of this absorption in the room is also of great importance. However, it is not possible to give general requirements that would be universally valid for all rooms and all possible uses. For example, in rooms used for speech or certain musical instruments such as drums, it is advisable to distribute the absorption as evenly as possible across all room surfaces in order to avoid flutter echo, as it can disrupt and degrade the quality of sound. On the other hand, activities such as singing or violin playing can sometimes benefit from flutter echo as it helps to sustain the sound.<br class=newline>
<p>So, we should remember that what is considered an acoustic fault for some uses can actually be a blessing for others! It goes to show that it is not easy (it can even be counterproductive) to stipulate a set of generalized requirements when it comes to “good acoustics”.
<p>
<h3 class=likesectionHead>Glossary</h3>
<div class=supertabular>
<dl>
  <dt>
    <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
        <mi mathvariant=italic>Lp</mi>
    </math>
  </dt>
  <dd>
    sound pressure level
  </dd>
</dl>
<dl>
  <dt>
    <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
        <mi mathvariant=italic>Lw</mi>
    </math>
  </dt>
  <dd>
    sound power level
  </dd>
</dl>
<dl>
  <dt>
    <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>α</mi>
    </math>
  </dt>
  <dd>
    absorption coefficient
  <dd>
</dl>
<dl>
  <dt>
    A
  </dt>
  <dd>
    Equivalent Absorption Area
  </dd>
</dl>
<dl>
  <dt>
    S
  </dt>
  <dd>
    surface area
  </dd>
</dl>
<dl>
  <dt>
    T
  </dt>
  <dd>
    reverberation time
  </dd>
</dl>
<dl>
  <dt>
    V
  </dt>
  <dd>
    volume of the room
  </dd>
</dl>
</div>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-6 class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>
      <p><span class=cmti-10>Influence of Size and Composition of the Orchestra on the Perception of </span><span class=cmti-10>Room Acoustical Quality</span>. WCSCS. Cambridge (USA), 1995.
   <dt id=X0-5 class=thebibliography>[2]
   <dd id=bib-2 class=thebibliography>
      <p>Internation Standard Organization. <span class=cmti-10>ISO 22955:2021, Acoustics — Acoustic </span><span class=cmti-10>quality of open office spaces</span>. 2021.
   <dt id=X0-4 class=thebibliography>[3]
   <dd id=bib-3 class=thebibliography>
      <p>Internation Standard Organization. <span class=cmti-10>ISO 23591:2021 Acoustic quality </span><span class=cmti-10>criteria for music rehearsal rooms and spaces</span>. 2021.
   <dt id=X0-3 class=thebibliography>[4]
   <dd id=bib-4 class=thebibliography>
      <p><span class=cmti-10>New Norwegian standard on the acoustics of rooms for music rehearsal and </span><span class=cmti-10>performance</span>. Forum Acusticum. Krakow, 2014.
   <dt id=X0-1 class=thebibliography>[5]
   <dd id=bib-5 class=thebibliography>
      <p>Norwegian Standard. <span class=cmti-10>NS 8178: 2014 Acoustic criteria for rooms and spaces </span><span class=cmti-10>for music rehearsal and performance</span>. 2014.
   <dt id=X0-2 class=thebibliography>[6]
   <dd id=bib-6 class=thebibliography>
      <p><span class=cmti-10>The music rehearsal room - for work and leisure</span>. Proceedings of ISRA. Amsterdam, 2019.
</dl>`;
/* eslint-enable */

export default theory;
